import type {
  User,
  SubscriptionPause,
  InactivityReset,
  Post,
  Group,
  Message,
  Report,
  ProgressPost,
  TrainingPlanTemplate,
  Food,
  UserTrainingPlan,
  Activity,
  Comment,
  MealLog,
  Meal,
  MealTemplate,
  ChatFolder,
} from "../types";
import { startOfWeek } from "date-fns";
import { INACTIVITY_BLOCK_ENABLED } from "./inactivity";
import { v4 as uuidv4 } from "uuid";
import { db, auth, app as mainApp, storage, functions } from "./firebase";
import { withTimeout, TimeoutError } from "./async";
import { diag, since } from "./diag";
import { discardFromOutbox, writeWithOutbox } from "./outbox";
import { loadProfileSnapshot, loadSignedInUidHint, loadUsersSnapshot, saveUsersSnapshot } from "./local-snapshot";
import {
  collection,
  getDocs,
  getDocsFromCache,
  getDocsFromServer,
  doc,
  getDoc,
  query,
  where,
  addDoc,
  onSnapshot,
  type Unsubscribe,
  type Query,
  type QuerySnapshot,
  type FirestoreError,
  orderBy,
  writeBatch,
  runTransaction,
  arrayUnion,
  arrayRemove,
  increment,
  deleteDoc,
  setDoc,
  updateDoc,
  limit,
  documentId,
  deleteField,
} from "@firebase/firestore";
import {
  createUserWithEmailAndPassword,
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  sendPasswordResetEmail,
  type Auth,
} from "@firebase/auth";
import { httpsCallable } from "@firebase/functions";
import { initializeApp, getApp, type FirebaseApp } from "@firebase/app";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type StorageReference,
  type UploadMetadata,
} from "@firebase/storage";
// import { createTrainingPlanTemplateFlow, getTrainingPlanTemplatesFlow } from '@/ai/flows/training-plan-template-flow';
import { findOrCreatePrivateGroupAction } from "./actions";
import foodsData from "../data/foods.json";
import { prepareImage } from "./image-prepare";
import { DAY_MS, subscriptionTimeLeft } from "./subscription";

// --- Users cache ---
// Every user document carries that user's full training and meal plans, so
// reading the whole collection is a big download. It used to be repeated for
// every feed/comment snapshot after any profile change, with no time limit,
// which is what left screens spinning until the app was restarted. Now there is
// at most one download in flight, the last known list is reused while it runs,
// and no screen waits on the server for more than a few seconds.
let usersMapCache: Map<string, User> | null = null;
let usersMapIsFresh = false;
// False while the list only holds the summaries saved on this device (no
// training plans, goals, ...).
let usersMapIsComplete = false;
let usersVersion = 0;
let usersServerLoad: Promise<Map<string, User>> | null = null;
const usersUpdatedListeners = new Set<(users: Map<string, User>) => void>();
const USERS_SERVER_WAIT_MS = 8000;

/** Marks the users list as outdated; it is still shown until reloaded. */
export function clearUsersCache() {
  usersMapIsFresh = false;
  usersVersion++;
}

/** Called whenever a newer users list has been loaded from the server. */
export function onUsersUpdated(listener: (users: Map<string, User>) => void): () => void {
  usersUpdatedListeners.add(listener);
  return () => {
    usersUpdatedListeners.delete(listener);
  };
}

function toUsersMap(docs: { id: string; data: () => unknown }[]): Map<string, User> {
  const usersMap = new Map<string, User>();
  docs.forEach((d) => {
    usersMap.set(d.id, { id: d.id, ...(d.data() as object) } as User);
  });
  return usersMap;
}

function loadUsersFromServer(): Promise<Map<string, User>> {
  if (!usersServerLoad) {
    const versionAtStart = usersVersion;
    const started = performance.now();
    usersServerLoad = getDocsFromServer(collection(db, "users"))
      .then((snapshot) => {
        const usersMap = toUsersMap(snapshot.docs);
        diag("users loaded from server", { count: usersMap.size, ms: since(started) });
        usersMapCache = usersMap;
        usersMapIsComplete = true;
        usersMapIsFresh = versionAtStart === usersVersion;
        if (auth.currentUser) saveUsersSnapshot(auth.currentUser.uid, usersMap.values());
        usersUpdatedListeners.forEach((listener) => listener(usersMap));
        return usersMap;
      })
      .catch((error) => {
        diag("users server load failed", { ms: since(started), code: (error as { code?: string })?.code });
        throw error;
      })
      .finally(() => {
        usersServerLoad = null;
      });
  }
  return usersServerLoad;
}

/**
 * The users list saved on this device by an earlier load (summaries only:
 * names, avatars, roles, subscription dates), or null if there is none.
 */
async function loadUsersFromDevice(): Promise<Map<string, User> | null> {
  const uid = auth.currentUser?.uid ?? loadSignedInUidHint();
  const saved = uid ? loadUsersSnapshot(uid) : null;
  if (!saved) return null;
  diag("users from device copy", { count: saved.length });
  return new Map(saved.map((u) => [u.id, u]));
}

/**
 * All users. With `preferFresh`, waits (briefly) for the server even when a
 * list is already in memory; otherwise returns the known list right away and
 * refreshes it in the background when outdated.
 */
async function getUsersMap({ preferFresh = false, complete = false } = {}): Promise<Map<string, User>> {
  const cacheUsable = usersMapCache && (usersMapIsComplete || !complete);
  if (usersMapCache && cacheUsable && (usersMapIsFresh || !preferFresh)) {
    if (!usersMapIsFresh) {
      loadUsersFromServer().catch((e) => console.warn("Background users refresh failed", e));
    }
    return usersMapCache;
  }
  const serverLoad = loadUsersFromServer();
  try {
    return await withTimeout(serverLoad, USERS_SERVER_WAIT_MS, "load users");
  } catch (error) {
    console.warn("Users are slow to load, using the last known list", error);
    serverLoad.catch(() => undefined);
    if (!complete) {
      if (usersMapCache) return usersMapCache;
      const fromDevice = await loadUsersFromDevice();
      if (fromDevice) {
        usersMapCache = usersMapCache ?? fromDevice;
        return usersMapCache;
      }
    } else if (usersMapCache && usersMapIsComplete) {
      return usersMapCache;
    }
    // Nothing usable yet: give the server a while longer.
    return await withTimeout(serverLoad, 20000, "load users");
  }
}

/**
 * The users list already available without the network (in memory or stored
 * on this device), for showing a screen instantly. Null when there is none.
 */
export async function getAllUsersQuick(): Promise<User[] | null> {
  if (usersMapCache) return Array.from(usersMapCache.values());
  const fromDevice = await loadUsersFromDevice();
  if (!fromDevice) return null;
  usersMapCache = usersMapCache ?? fromDevice;
  return Array.from(usersMapCache.values());
}

// Single users for display (post and comment authors), looked up by id
// instead of downloading everyone. Uses a query because regular users may list
// users but not read other users' documents directly.
const userDisplayCache = new Map<string, User>();

async function fetchUsersByIds(ids: string[]): Promise<User[]> {
  const q = query(collection(db, "users"), where(documentId(), "in", ids));
  const started = performance.now();
  try {
    const snapshot = await withTimeout(getDocs(q), 6000, "load users by id");
    if (since(started) > 1500) diag("slow author lookup", { ids: ids.length, ms: since(started) });
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as User);
  } catch (error) {
    console.warn("Slow user lookup, using cached copies", error);
    try {
      const cached = await withTimeout(getDocsFromCache(q), 3000, "cached users by id");
      return cached.docs.map((d) => ({ id: d.id, ...d.data() }) as User);
    } catch {
      return [];
    }
  }
}

export async function getUsersByIds(ids: string[]): Promise<Map<string, User>> {
  const result = new Map<string, User>();
  const missing: string[] = [];
  for (const id of new Set(ids)) {
    if (!id) continue;
    const known = usersMapCache?.get(id) ?? userDisplayCache.get(id);
    if (known) result.set(id, known);
    else missing.push(id);
  }
  const chunks: string[][] = [];
  for (let i = 0; i < missing.length; i += 30) chunks.push(missing.slice(i, i + 30));
  const loaded = await Promise.all(chunks.map(fetchUsersByIds));
  loaded.flat().forEach((u) => {
    userDisplayCache.set(u.id, u);
    result.set(u.id, u);
  });
  return result;
}

const UNKNOWN_USER: User = {
  id: "unknown",
  name: "Unknown User",
  email: "",
  role: "user",
  avatar: "",
};

// Foods in-memory cache
let foodsCacheByUser: Map<string | undefined, Food[]> = new Map();

// The food list is also kept on the device for a while: reading the few
// hundred shared foods from the database blocks a phone for up to a second.
const FOODS_DEVICE_KEY = "nn-foods-v1-";
const FOODS_DEVICE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

function readFoodsFromDevice(userId: string): Food[] | null {
  try {
    const saved = JSON.parse(localStorage.getItem(FOODS_DEVICE_KEY + userId) || "null");
    if (!saved || !Array.isArray(saved.foods) || Date.now() - saved.savedAt > FOODS_DEVICE_MAX_AGE_MS) return null;
    return saved.foods as Food[];
  } catch {
    return null;
  }
}

function saveFoodsOnDevice(userId: string, foods: Food[]) {
  try {
    // One user's list at a time (someone else may have used this device).
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(FOODS_DEVICE_KEY)) localStorage.removeItem(key);
    }
    localStorage.setItem(FOODS_DEVICE_KEY + userId, JSON.stringify({ savedAt: Date.now(), foods }));
  } catch {
    // Storage full: the list is loaded from the database next time.
  }
}

// Bumped by clearFoodsCache: a load that started before (e.g. while a food
// was being deleted) mustn't put its old list back into the cache.
let foodsCacheGeneration = 0;

export function clearFoodsCache() {
  foodsCacheGeneration++;
  foodsInflight.clear();
  foodsCacheByUser.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(FOODS_DEVICE_KEY)) localStorage.removeItem(key);
    }
  } catch {
    // Nothing saved.
  }
}

// --- Auth / Users ---
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export async function findUserById(id: string): Promise<User | undefined> {
  const userRef = doc(db, "users", id);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) return { id: userSnap.id, ...userSnap.data() } as User;
  return undefined;
}

/**
 * findUserById for app start / login. A server read can stall indefinitely on
 * a bad connection, which left the app on "Loading..." forever; after a few
 * seconds fall back to the locally cached copy of the profile.
 */
/**
 * findUserById for app start / login. A server read can stall on a bad
 * connection, which left the app on "Loading..." forever; after a few seconds
 * use the copy of the profile saved on this device instead, and hand over the
 * server's copy through `onLateServerCopy` if it arrives later.
 */
export async function findUserByIdResilient(
  id: string,
  onLateServerCopy?: (user: User | undefined) => void,
): Promise<User | undefined> {
  const userRef = doc(db, "users", id);
  const started = performance.now();
  const serverRead = getDoc(userRef).then((snap) =>
    snap.exists() ? ({ id: snap.id, ...snap.data() } as User) : undefined,
  );
  try {
    const user = await withTimeout(serverRead, 6000, "load user");
    diag("own profile loaded", { ms: since(started), found: !!user });
    return user;
  } catch (error) {
    const saved = loadProfileSnapshot(id);
    diag("own profile slow", { ms: since(started), usingDeviceCopy: !!saved });
    if (saved) {
      serverRead
        .then((user) => {
          diag("own profile arrived late", { ms: since(started) });
          onLateServerCopy?.(user);
        })
        .catch(() => undefined);
      return saved;
    }
    // Nothing saved on this device: keep waiting for the server (the loading
    // screen offers a restart after a while).
    return await serverRead;
  }
}

export async function findUserByEmail(
  email: string,
): Promise<User | undefined> {
  try {
    const usersRef = collection(db, "users");
    const qUsers = query(usersRef, where("email", "==", email));
    const qSnap = await getDocs(qUsers);
    if (!qSnap.empty) {
      const d = qSnap.docs[0];
      const data = d.data();
      return { id: d.id, ...data } as User;
    }
    return undefined;
  } catch (e) {
    console.error("Error fetching user by email:", e);
    return undefined;
  }
}

// --- Posts ---
export function getPosts(
  callback: (posts: (Post & { author: User })[], hasMore: boolean, fromCache: boolean) => void,
  options: { limitCount?: number; onError?: (error: Error) => void } = {},
): Unsubscribe {
  const { limitCount, onError } = options;
  const postsCol = collection(db, "posts");
  const q = limitCount
    ? query(postsCol, orderBy("createdAt", "desc"), limit(limitCount))
    : query(postsCol, orderBy("createdAt", "desc"));
  // Author lookups are async; an older snapshot must not overwrite a newer one.
  let latestSnapshot = 0;
  const started = performance.now();
  const unsubscribe = onServerSnapshot(
    q,
    async (snapshot) => {
      const snapshotNumber = ++latestSnapshot;
      const posts = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Post,
      );
      const authors = await getUsersByIds(posts.map((post) => post.authorId));
      if (snapshotNumber !== latestSnapshot) return;
      if (snapshotNumber === 1) {
        diag("feed loaded", { posts: posts.length, ms: since(started), fromCache: snapshot.metadata.fromCache });
      }
      callback(
        posts.map((post) => ({
          ...post,
          author: authors.get(post.authorId) || UNKNOWN_USER,
        })),
        !!limitCount && snapshot.size >= limitCount,
        snapshot.metadata.fromCache,
      );
    },
    (err) => {
      diag("feed listener failed", { code: (err as { code?: string }).code });
      console.error("Posts error:", err);
      if (onError) onError(err);
      else callback([], false, false);
    },
  );
  return unsubscribe;
}

// A Firestore write resolves only once the server confirms it. On a dead
// connection that's when the connection returns, and the "Objavi" button
// spun until then. The write itself is kept and sent later (posts and
// comments also survive the app being closed, see lib/outbox.ts), so after a
// while the screen carries on as if it were done.
const WRITE_WAIT_MS = 12_000;
async function writeOrQueue(write: Promise<unknown>, label: string): Promise<void> {
  try {
    await withTimeout(write, WRITE_WAIT_MS, label);
  } catch (error) {
    if (!(error instanceof TimeoutError)) throw error;
    diag("write queued", { label });
    write.catch((later) => console.error(`${label} failed after going through offline`, later));
  }
}

export async function createPost(
  authorId: string,
  content: string,
  mediaUrls: string[],
  mediaType: "image" | "video" | null,
  options: { id?: string; extra?: Partial<Post> } = {},
): Promise<Post> {
  const newPostRef = options.id
    ? doc(db, "posts", options.id)
    : doc(collection(db, "posts"));
  const newPostData: Omit<Post, "id"> & { mediaType?: "image" | "video" } = {
    ...options.extra,
    authorId,
    content,
    createdAt: new Date().toISOString(),
    likes: 0,
    likedBy: [],
    imageUrls: mediaType === "image" ? mediaUrls : [],
    videoUrls: mediaType === "video" ? mediaUrls : [],
  };
  if (mediaType) newPostData.mediaType = mediaType;
  await writeOrQueue(writeWithOutbox({ path: newPostRef.path, data: newPostData }), "create post");
  return { id: newPostRef.id, ...newPostData } as Post;
}

export async function deletePost(postId: string): Promise<void> {
  const postRef = doc(db, "posts", postId);
  // Not sent yet: just make sure it never is.
  if (discardFromOutbox(postRef.path)) return;
  const postSnap = await getDoc(postRef);
  if (!postSnap.exists()) return;
  const postData = postSnap.data() as Post;
  const mediaUrls = [
    ...(postData.imageUrls || []),
    ...(postData.videoUrls || []),
    ...(postData.videoThumbnailUrl ? [postData.videoThumbnailUrl] : []),
  ];
  await Promise.all(
    mediaUrls.map((url) => deleteObject(ref(storage, url)).catch(() => { })),
  );
  await deleteDoc(postRef);
}

export async function toggleLikePost(
  postId: string,
  userId: string,
): Promise<void> {
  const postRef = doc(db, "posts", postId);
  await runTransaction(db, async (transaction) => {
    const postDoc = await transaction.get(postRef);
    if (!postDoc.exists()) throw "Document does not exist!";
    const postData = postDoc.data() as Post;
    const isLiked = (postData.likedBy || []).includes(userId);
    transaction.update(postRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId),
    });
  });
}

// --- Comments ---
export function getComments(
  postId: string,
  callback: (comments: (Comment & { author: User })[]) => void,
): Unsubscribe {
  const commentsCol = collection(db, "posts", postId, "comments");
  const q = query(commentsCol, orderBy("createdAt", "asc"));
  let latestSnapshot = 0;
  return onSnapshot(
    q,
    async (snapshot) => {
      const comments = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Comment,
      );
      const snapshotNumber = ++latestSnapshot;
      const authors = await getUsersByIds(comments.map((comment) => comment.authorId));
      if (snapshotNumber !== latestSnapshot) return;
      callback(
        comments.map((comment) => ({
          ...comment,
          author: authors.get(comment.authorId) || UNKNOWN_USER,
        })),
      );
    },
    (err) => {
      console.error("Comments error:", err);
      callback([]);
    },
  );
}

export async function createComment(
  postId: string,
  authorId: string,
  content: string,
  mediaUrl?: string,
  mediaType?: "image" | "video" | "gif" | "sticker",
  options: { id?: string; extra?: Partial<Comment> } = {},
): Promise<Comment> {
  const commentsCol = collection(db, "posts", postId, "comments");
  const newCommentData: Omit<Comment, "id"> = {
    ...options.extra,
    postId,
    authorId,
    content,
    createdAt: new Date().toISOString(),
    likes: 0,
    likedBy: [],
  };
  if (mediaUrl) {
    newCommentData.mediaUrl = mediaUrl;
    newCommentData.mediaType = mediaType || "image";
  }

  const docRef = options.id ? doc(commentsCol, options.id) : doc(commentsCol);
  // The comment and the post's comment count go together (both or neither).
  await writeOrQueue(
    writeWithOutbox({ path: docRef.path, data: newCommentData, countPostId: postId }),
    "create comment",
  );

  return { id: docRef.id, ...newCommentData } as Comment;
}

export async function deleteComment(
  postId: string,
  commentId: string,
): Promise<void> {
  const commentRef = doc(db, "posts", postId, "comments", commentId);
  // Not sent yet (so not counted either): just make sure it never is.
  if (discardFromOutbox(commentRef.path)) return;
  await deleteDoc(commentRef);

  // Decrement comment count on the post atomically
  await updateDoc(doc(db, "posts", postId), {
    commentCount: increment(-1)
  });
}

export async function toggleLikeComment(
  postId: string,
  commentId: string,
  userId: string,
): Promise<void> {
  const commentRef = doc(db, "posts", postId, "comments", commentId);
  await runTransaction(db, async (transaction) => {
    const commentDoc = await transaction.get(commentRef);
    if (!commentDoc.exists()) throw "Document does not exist!";
    const commentData = commentDoc.data() as Comment;
    const isLiked = (commentData.likedBy || []).includes(userId);
    transaction.update(commentRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId),
    });
  });
}

// --- Groups ---
export async function getGroups(): Promise<Group[]> {
  const groupsRef = collection(db, "groups");
  // Server first: the local cache only holds the groups this device has seen
  // (e.g. the trainer's own chats), so reading it first hid all other groups.
  let snapshot;
  try {
    snapshot = await withTimeout(getDocsFromServer(groupsRef), 10000, "load groups");
  } catch (e) {
    snapshot = await getDocsFromCache(groupsRef);
  }
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Group);
}

export async function getGroupsForUser(userId: string): Promise<Group[]> {
  const groupsRef = collection(db, "groups");
  const q = query(groupsRef, where("members", "array-contains", userId));
  // Use cache-first, fallback to server
  let snapshot;
  try {
    snapshot = await getDocsFromCache(q);
    if (snapshot.empty) {
      snapshot = await getDocsFromServer(q);
    }
  } catch (e) {
    snapshot = await getDocsFromServer(q);
  }
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Group);
}

/**
 * onSnapshot for screens that show a spinner until the list has loaded.
 *
 * Firestore only caches in memory (see firebase.ts), so an empty result
 * "from cache" before anything was shown just means the server hasn't
 * answered yet (e.g. while reconnecting), not an empty list. Firestore only
 * reports the server's later "still empty" answer to listeners that include
 * metadata changes, so this includes them and then skips the snapshots where
 * only metadata changed (which plain listeners never got).
 */
export function onServerSnapshot<T>(
  q: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  let delivered = false;
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      if (!delivered && snapshot.metadata.fromCache && snapshot.empty) return;
      if (delivered && snapshot.docChanges().length === 0) return;
      delivered = true;
      onNext(snapshot);
    },
    onError,
  );
}

export function getMessagesForGroup(
  groupId: string,
  callback: (messages: Message[]) => void,
  limitCount: number = 100,
  onError?: (error: Error) => void,
): Unsubscribe {
  const messagesRef = collection(db, "groups", groupId, "messages");
  // We fetch DESCending to get the latest messages, then reverse them
  const q = query(messagesRef, orderBy("createdAt", "desc"), limit(limitCount));
  const unsubscribe = onServerSnapshot(
    q,
    (snapshot) => {
      const msgs = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Message,
      );
      // Reverse to get generic chronological order (Oldest -> Newest)
      callback(msgs.reverse());
    },
    (err) => {
      console.error(err);
      // A failed listener is dead for good. Let the caller resubscribe instead
      // of wiping the chat (which made every message "disappear").
      if (onError) onError(err);
      else callback([]);
    },
  );
  return unsubscribe;
}

// --- File Uploads ---
// An upload that sends nothing for this long is stopped with an error. The
// SDK itself keeps retrying for up to 10 minutes, which looked like an
// endless spinner (Android users saving a progress photo on a bad connection).
const UPLOAD_STALL_MS = 45_000;
const DOWNLOAD_URL_TIMEOUT_MS = 30_000;

export class UploadStalledError extends Error {
  constructor() {
    super("Slanje je zapelo. Provjerite internetsku vezu i pokušajte ponovno.");
    this.name = "UploadStalledError";
  }
}

function putFile(storageRef: StorageReference, file: Blob, metadata: UploadMetadata): Promise<void> {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, metadata);
    let lastProgressAt = Date.now();
    let lastBytes = -1;
    let settled = false;
    const done = (error?: unknown) => {
      if (settled) return;
      settled = true;
      clearInterval(watchdog);
      if (error) reject(error);
      else resolve();
    };
    const watchdog = setInterval(() => {
      // Hidden app: the system may pause the network; don't count that time.
      if (document.visibilityState === "hidden") lastProgressAt = Date.now();
      if (Date.now() - lastProgressAt > UPLOAD_STALL_MS) {
        diag("upload stalled", { path: storageRef.fullPath.split("/")[0], kb: Math.round(file.size / 1024), sentKb: Math.round(Math.max(0, lastBytes) / 1024) });
        done(new UploadStalledError());
        task.cancel();
      }
    }, 5_000);
    task.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.bytesTransferred !== lastBytes) {
          lastBytes = snapshot.bytesTransferred;
          lastProgressAt = Date.now();
        }
      },
      (error) => done(error),
      () => done(),
    );
  });
}

function downloadUrlOf(storageRef: StorageReference): Promise<string> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new UploadStalledError()), DOWNLOAD_URL_TIMEOUT_MS);
    getDownloadURL(storageRef).then(
      (url) => {
        clearTimeout(timer);
        resolve(url);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

// A "/" in a picked file's name would make an extra folder in the storage
// path, which the storage rules then reject.
function safeFileName(name: string): string {
  const cleaned = Array.from(name || "file", (ch) => (ch < " " || "/\\#?%".includes(ch) ? "_" : ch)).join("");
  return cleaned.slice(-120) || "file";
}

async function uploadFileForUser(
  path: string,
  file: File,
  userId: string,
): Promise<string> {
  if (!auth.currentUser) throw new Error("Not authenticated");
  const storageRef = ref(
    storage,
    `${path}/${userId}/${Date.now()}-${safeFileName(file.name)}`,
  );
  await putFile(storageRef, file, {
    cacheControl: "public, max-age=31536000, immutable",
    contentType: file.type || undefined,
  });
  return downloadUrlOf(storageRef);
}
async function uploadFile(path: string, file: File, metadata?: Record<string, string>): Promise<string> {
  if (!auth.currentUser) throw new Error("Not authenticated");
  const storageRef = ref(
    storage,
    `${path}/${auth.currentUser.uid}/${Date.now()}-${safeFileName(file.name)}`,
  );
  await putFile(storageRef, file, {
    cacheControl: "public, max-age=31536000, immutable",
    contentType: file.type || undefined,
    customMetadata: metadata,
  });
  return downloadUrlOf(storageRef);
}

export async function uploadMultiplePostMedia(
  files: File[],
): Promise<string[]> {
  const processedFiles = await Promise.all(
    files.map(async (f) => {
      if (f.type.startsWith("image/")) {
        return await convertImageToJPEG(f);
      }
      return f;
    }),
  );
  return Promise.all(processedFiles.map((f) => uploadFile("posts", f)));
}
export async function uploadDietPlan(
  userId: string,
  file: File,
): Promise<string> {
  return uploadFileForUser("diet-plans", file, userId);
}
export async function uploadPostImage(file: File): Promise<string> {
  const converted = await convertImageToJPEG(file);
  return uploadFile("posts", converted);
}
export async function uploadCommentMedia(file: File): Promise<string> {
  if (file.type.startsWith("image/")) {
    const converted = await convertImageToJPEG(file);
    return uploadFile("comments", converted);
  }
  return uploadFile("comments", file);
}
export async function uploadReportImage(file: File): Promise<string> {
  const converted = await convertImageToJPEG(file);
  return uploadFile("reports", converted);
}
// Avatars show at most ~100 px (x3 on phones); a full-size photo made every
// list of people download megabytes.
const AVATAR_MAX_SIDE = 512;

export async function uploadGroupAvatar(file: File): Promise<string> {
  const converted = await prepareImage(file, AVATAR_MAX_SIDE);
  return uploadFile("group-avatars", converted);
}

/**
 * A JPEG version of a picked photo, ready to upload (see lib/image-prepare.ts).
 * Throws ImagePrepareError, with a message to show, if it can't be read.
 */
export function convertImageToJPEG(file: File): Promise<File> {
  return prepareImage(file);
}

export async function uploadUserAvatar(file: File): Promise<string> {
  const convertedFile = await prepareImage(file, AVATAR_MAX_SIDE);

  // Delete old avatar from Storage before uploading new one (non-blocking)
  if (auth.currentUser) {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const oldAvatar = userSnap.data()?.avatar as string | undefined;
      if (oldAvatar && oldAvatar.startsWith("https://firebasestorage.googleapis.com")) {
        deleteObject(ref(storage, oldAvatar)).catch(() => { /* ignore if already deleted */ });
      }
    } catch { /* non-blocking */ }
  }

  const url = await uploadFile("user-avatars", convertedFile);

  // Log activity if this is the first avatar set (matching Next.js behavior)
  if (auth.currentUser) {
    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        // Only create activity if profile setup is not yet completed
        if (!userData.profileSetupCompleted) {
          const { createActivityAction } = await import("./actions");
          await createActivityAction(userData, "first_avatar_set");
        }
      }
    } catch (error) {
      // Non-blocking - log but don't fail avatar upload
      console.warn("Failed to log first_avatar_set activity:", error);
    }
  }

  return url;
}
export async function uploadProgressImages(
  userId: string,
  files: File[],
): Promise<string[]> {
  const convertedFiles = await Promise.all(
    files.map((f) => convertImageToJPEG(f)),
  );
  return Promise.all(
    convertedFiles.map((f) => uploadFileForUser("progress-images", f, userId)),
  );
}
// Images and other files only; videos go through startVideoUpload (lib/video-upload.ts).
export async function uploadChatAttachment(file: File, groupId?: string): Promise<{ url: string }> {
  let fileToUpload = file;
  if (file.type.startsWith("image/")) {
    fileToUpload = await convertImageToJPEG(file);
  }

  const url = await uploadFile(
    fileToUpload.type.startsWith("image/") ? "chat-images" : "chat-files",
    fileToUpload,
    groupId ? { groupId } : undefined,
  );

  return { url };
}


// --- Reports ---
export async function createReport(
  reportedById: string,
  type: Report["type"],
  description: string,
  imageUrl?: string,
): Promise<void> {
  const reportsCol = collection(db, "reports");
  const reportData: any = {
    reportedById,
    type,
    description,
    createdAt: new Date().toISOString(),
    status: "open",
  };

  // Only add imageUrl if it exists
  if (imageUrl) {
    reportData.imageUrl = imageUrl;
  }

  await addDoc(reportsCol, reportData);
}

export const updateTrainingPlanTemplate = async (
  templateId: string,
  templateData: Partial<TrainingPlanTemplate>,
) => {
  await updateDoc(
    doc(db, "trainingPlanTemplates", templateId),
    sanitizeForFirestore({
      ...templateData,
      updatedAt: new Date().toISOString(),
    }),
  );
};

export async function getReports(): Promise<Report[]> {
  const snapshot = await getDocs(
    query(collection(db, "reports"), orderBy("createdAt", "desc")),
  );
  const reporters = await getUsersByIds(
    snapshot.docs.map((d) => d.data().reportedById as string),
  );
  return snapshot.docs.map(
    (d) =>
      ({
        id: d.id,
        ...d.data(),
        reportedBy: reporters.get(d.data().reportedById) || UNKNOWN_USER,
      }) as Report,
  );
}
export async function updateReportStatus(
  reportId: string,
  status: Report["status"],
): Promise<void> {
  const reportRef = doc(db, "reports", reportId);
  await updateDoc(reportRef, { status });
}

export async function deleteReport(reportId: string): Promise<void> {
  const reportRef = doc(db, "reports", reportId);
  await deleteDoc(reportRef);
}

// --- Chat Folders ---
export async function createChatFolder(
  name: string,
  createdBy: string,
  startDate: string,
  endDate: string,
): Promise<ChatFolder> {
  const usersCollection = collection(db, "users");
  const start = new Date(startDate).toISOString();
  
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  const endIso = end.toISOString();

  const q = query(
    usersCollection,
    where("createdAt", ">=", start),
    where("createdAt", "<=", endIso)
  );
  
  const snap = await getDocs(q);
  const userIds = snap.docs.map(doc => doc.id).filter(id => id !== createdBy);

  const newFolder: ChatFolder = {
    id: uuidv4(),
    name,
    createdBy,
    userIds,
    createdAt: new Date().toISOString()
  };

  const userRef = doc(db, "users", createdBy);
  await updateDoc(userRef, {
    chatFolders: arrayUnion(newFolder)
  });

  return newFolder;
}

export async function deleteChatFolder(folderId: string): Promise<void> {
  const userId = auth.currentUser?.uid;
  if (!userId) return;
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;
  
  const userData = userSnap.data();
  const folders: ChatFolder[] = userData.chatFolders || [];
  const folderToDelete = folders.find(f => f.id === folderId);
  
  if (folderToDelete) {
    await updateDoc(userRef, {
      chatFolders: arrayRemove(folderToDelete)
    });
  }
}

export function subscribeToChatFolders(
  userId: string,
  callback: (folders: ChatFolder[]) => void
): Unsubscribe {
  const userRef = doc(db, "users", userId);
  return onSnapshot(userRef, (docSnap) => {
    if (docSnap.exists()) {
      const userData = docSnap.data();
      callback(userData.chatFolders || []);
    } else {
      callback([]);
    }
  }, (err) => {
    console.error("Error subscribing to chat folders:", err);
    callback([]);
  });
}

// --- Groups CRUD ---
export async function createGroup(
  name: string,
  description: string,
  createdBy: string,
  members: string[],
  avatar?: string,
): Promise<Group> {
  const docRef = await addDoc(collection(db, "groups"), {
    name,
    description,
    createdBy,
    members,
    avatar: avatar || "💪",
    isPrivate: false,
  });
  return {
    id: docRef.id,
    name,
    description,
    createdBy,
    members,
    avatar: avatar || "💪",
    isPrivate: false,
  };
}
export async function deleteGroup(groupId: string): Promise<void> {
  const groupRef = doc(db, "groups", groupId);
  await deleteDoc(groupRef);
}
export async function addMembersToGroup(
  groupId: string,
  memberIds: string[],
): Promise<void> {
  if (memberIds.length === 0) return;
  const groupRef = doc(db, "groups", groupId);
  const batch = writeBatch(db);
  batch.update(groupRef, { members: arrayUnion(...memberIds) });
  await batch.commit();
}
export async function findOrCreatePrivateGroup(
  currentUserId: string,
  otherUserId: string,
): Promise<Group | null> {
  return await findOrCreatePrivateGroupAction(currentUserId, otherUserId);
}
export async function updateGroup(
  groupId: string,
  data: { name?: string; avatar?: string; description?: string },
): Promise<void> {
  const groupRef = doc(db, "groups", groupId);
  await updateDoc(groupRef, data);
}
export async function removeUserFromGroup(
  groupId: string,
  userIdToRemove: string,
): Promise<void> {
  const groupRef = doc(db, "groups", groupId);
  await updateDoc(groupRef, { members: arrayRemove(userIdToRemove) });
}

// --- User Profile ---
export async function updateUserProfile(
  userId: string,
  data: Partial<User>,
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, data);
  clearUsersCache();
}
/**
 * All users, as fresh as possible: waits a few seconds for the server, then
 * falls back to the last known list. Throws only if nothing can be loaded.
 */
/**
 * All users. Pass `complete` when full user documents are needed (plans,
 * goals): otherwise, on a slow connection, the summaries saved on this device
 * may be returned.
 */
export async function getAllUsers({ complete = false } = {}): Promise<User[]> {
  const usersMap = await getUsersMap({ preferFresh: true, complete });
  return Array.from(usersMap.values());
}

// --- User Creation / Temp Auth ---
let tempAuthInstance: Auth | null = null;
function getTempAuth(): Auth {
  if (tempAuthInstance) return tempAuthInstance;
  let tempApp: FirebaseApp;
  try {
    tempApp = getApp("createUserApp");
  } catch {
    tempApp = initializeApp(mainApp.options, "createUserApp");
  }
  try {
    // In-memory only: no IndexedDB and no sign-in iframe, which made creating
    // accounts slow and flaky in the iOS WebView.
    tempAuthInstance = initializeAuth(tempApp, { persistence: inMemoryPersistence });
  } catch {
    tempAuthInstance = getAuth(tempApp);
  }
  return tempAuthInstance;
}

const DEFAULT_AVATAR = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLXVzZXItcm91bmQiPjxjaXJjbGUgY3g9IjEyIiBjeT0iOCIgcj0iNSIvPjxwYXRoIGQ9Ik0yMCAyMWE4IDggMCAwIDAtMTYgMCIvPjwvc3ZnPg==`;

function buildNewUserDoc(
  name: string,
  email: string,
  role: "user" | "trainer" | "glavni_trener",
  phone: string,
  trainedBy?: string,
  subscriptionDays?: number,
): Omit<User, "id"> {
  const now = new Date();
  const finalTrainedBy = role === "user" ? trainedBy || "Neoznačeno" : "Neoznačeno";
  // Ensure subscriptionDays is a number to prevent string concatenation bugs
  const daysToAdd = subscriptionDays ? Number(subscriptionDays) : 0;
  let subscriptionEndDate: Date;
  if (role === "user" && daysToAdd > 0) {
    subscriptionEndDate = new Date();
    subscriptionEndDate.setDate(now.getDate() + daysToAdd);
  } else {
    subscriptionEndDate = new Date(0); // Expired date
  }
  return {
    name,
    email,
    phone,
    role,
    avatar: DEFAULT_AVATAR,
    createdAt: now.toISOString(),
    subscriptionEndDate: subscriptionEndDate.toISOString(),
    trainedBy: finalTrainedBy,
    profileSetupCompleted: false, // Set to false for new users
  };
}

function isCallableUnavailable(error: any): boolean {
  // "not-found": function not deployed. On web a missing function fails CORS
  // and surfaces as "internal". "permission-denied": the function can't be
  // invoked yet (Cloud Run invoker not configured); non-staff callers fail the
  // client-side path's security rules anyway.
  return (
    error?.code === "functions/not-found" ||
    error?.code === "functions/internal" ||
    error?.code === "functions/permission-denied"
  );
}

/** A user profile with this email created in the last few minutes, if any. */
async function findJustCreatedUser(email: string): Promise<User | null> {
  try {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", email), limit(1)));
    if (snap.empty) return null;
    const user = { id: snap.docs[0].id, ...snap.docs[0].data() } as User;
    const created = Date.parse(user.createdAt || "");
    return Date.now() - created < 5 * 60 * 1000 ? user : null;
  } catch {
    return null;
  }
}

// Fallback used until the createUserAccount function is deployed.
async function createUserOnClient(newUser: Omit<User, "id">): Promise<string> {
  const tempAuth = getTempAuth();
  const credential = await createUserWithEmailAndPassword(
    tempAuth,
    newUser.email,
    // Temporary; the user sets their own password from the setup email.
    `${uuidv4()}Aa1!`,
  );
  try {
    await setDoc(doc(db, "users", credential.user.uid), newUser);
    return credential.user.uid;
  } catch (e) {
    // Don't leave the email registered in Auth without an app account —
    // that blocked re-adding the user ("email already in use").
    console.error("Profile write failed, rolling back auth account", e);
    await credential.user.delete().catch((deleteError) =>
      console.error("Rollback of auth account failed", deleteError),
    );
    throw e;
  } finally {
    await tempAuth.signOut().catch(() => undefined);
  }
}

export async function createUser(
  name: string,
  email: string,
  role: "user" | "trainer" | "glavni_trener",
  _creator: User,
  phone: string,
  trainedBy?: string,
  subscriptionDays?: number,
  paymentPlan: NonNullable<User["paymentPlan"]> = "jednokratno",
): Promise<CreatedUser> {
  const normalizedEmail = email.trim().toLowerCase();
  const trimmedName = name.trim();

  let createdUser: User;
  let activation: ActivationResult | null = null;
  try {
    // Preferred: the server creates the Auth account and the profile together
    // (and repairs accounts that were only half-created before).
    const call = httpsCallable<unknown, { user: User; activation?: ActivationResult | null }>(
      functions,
      "createUserAccount",
    );
    const result = await call({
      name: trimmedName,
      email: normalizedEmail,
      role,
      phone,
      trainedBy,
      subscriptionDays: subscriptionDays ? Number(subscriptionDays) : 0,
      paymentPlan,
      sendActivation: true,
    });
    createdUser = result.data.user;
    activation = result.data.activation ?? null;
  } catch (error: any) {
    if (error?.code === "functions/already-exists") {
      const alreadyExists = new Error("Email adresa se već koristi.") as Error & { code: string };
      alreadyExists.code = "auth/email-already-in-use";
      throw alreadyExists;
    }
    if (!isCallableUnavailable(error)) {
      console.error("createUserAccount failed", error);
      throw error;
    }
    console.warn("createUserAccount unavailable, creating user on the client", error);
    const newUser = buildNewUserDoc(trimmedName, normalizedEmail, role, phone, trainedBy, subscriptionDays);
    try {
      const uid = await createUserOnClient(newUser);
      createdUser = { id: uid, ...newUser };
    } catch (clientError: any) {
      // "internal" can also mean the server did create the user and only its
      // answer got lost; then the email is taken by that new account. Use it,
      // so the setup email below is still sent.
      const justCreated =
        clientError?.code === "auth/email-already-in-use"
          ? await findJustCreatedUser(normalizedEmail)
          : null;
      if (!justCreated) throw clientError;
      createdUser = justCreated;
    }
  }

  // Set here rather than by the server so it works with the deployed
  // createUserAccount function as it is. Not fatal: the user exists by now,
  // and the plan can be set on their profile.
  if (role === "user" && paymentPlan !== "jednokratno") {
    try {
      await updateDoc(doc(db, "users", createdUser.id), { paymentPlan });
      createdUser = { ...createdUser, paymentPlan };
    } catch (planError) {
      console.error("Saving the payment plan failed", planError);
    }
  }

  // The server emails our activation link. Without it (server not updated
  // yet, or creating the link failed) fall back to Firebase's password reset
  // email. Not fatal if that fails: the email can be sent again.
  if (!activation) {
    try {
      await sendPasswordReset(normalizedEmail);
    } catch (emailError) {
      console.warn(
        "Password reset email failed, but user was created:",
        emailError,
      );
      // Don't throw - user creation succeeded, admin can resend email manually
    }
  }

  // Trigger onboarding messages (non-blocking - don't fail if this errors)
  try {
    const { triggerOnboardingFlowAction } = await import("./actions");
    const result = await withTimeout(
      triggerOnboardingFlowAction(createdUser.id, "initial"),
      20000,
      "onboarding",
    );
    if (!result.success) {
      console.warn(
        "Onboarding flow returned failure, but user was created:",
        result,
      );
    }
  } catch (onboardingError: any) {
    // Log but don't throw - user creation succeeded
    console.error(
      "Onboarding flow failed, but user was created:",
      onboardingError,
    );
  }

  clearUsersCache();
  return { user: createdUser, activation };
}

// --- Account activation (functions/src/activation.ts) ---

export interface ActivationResult {
  /** Whether the activation email went out. */
  sent: boolean;
  /** The link itself, to send another way if the email didn't. */
  link: string;
}

export interface CreatedUser {
  user: User;
  /** Null when only Firebase's password reset email was sent. */
  activation: ActivationResult | null;
}

export type ActivationInfo =
  | { status: "ok"; name: string; email: string }
  | { status: "used" }
  | { status: "invalid" };

export async function getActivation(token: string): Promise<ActivationInfo> {
  const call = httpsCallable<{ token: string }, ActivationInfo>(functions, "getActivation");
  return (await call({ token })).data;
}

/** Sets the password from an activation link; returns the account's email. */
export async function activateAccount(token: string, password: string): Promise<string> {
  const call = httpsCallable<{ token: string; password: string }, { email: string }>(
    functions,
    "activateAccount",
  );
  return (await call({ token, password })).data.email;
}

/** Staff: a new activation link for a user who hasn't activated yet. */
export async function sendActivationEmail(userId: string): Promise<ActivationResult> {
  const call = httpsCallable<{ uid: string }, ActivationResult>(functions, "sendActivationEmail");
  return (await call({ uid: userId })).data;
}

/**
 * "Nisi dobio mail za aktivaciju računa?": the server sends a new activation
 * link, or a password reset link if the account is already in use. Until it
 * can send email, Firebase's password reset email does the job as before.
 */
export async function requestActivationEmail(email: string): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  try {
    const call = httpsCallable<{ email: string }, { ok: boolean }>(functions, "requestActivationEmail");
    await call({ email: normalizedEmail });
  } catch (error: any) {
    if (error?.code !== "functions/unavailable" && !isCallableUnavailable(error)) throw error;
    await sendPasswordReset(normalizedEmail);
  }
}

// --- Payment plan (lib/installments.ts) ---

export async function setUserPaymentPlan(
  userId: string,
  paymentPlan: NonNullable<User["paymentPlan"]>,
): Promise<void> {
  await updateDoc(doc(db, "users", userId), { paymentPlan });
  clearUsersCache();
}

/** Confirms (or, with `confirmedBy` null, takes back) the second installment. */
export async function setSecondInstallmentPaid(
  userId: string,
  confirmedBy: string | null,
): Promise<Pick<User, "secondInstallmentPaidAt" | "secondInstallmentConfirmedBy">> {
  if (confirmedBy === null) {
    await updateDoc(doc(db, "users", userId), {
      secondInstallmentPaidAt: deleteField(),
      secondInstallmentConfirmedBy: deleteField(),
    });
    clearUsersCache();
    return { secondInstallmentPaidAt: undefined, secondInstallmentConfirmedBy: undefined };
  }
  const paid = {
    secondInstallmentPaidAt: new Date().toISOString(),
    secondInstallmentConfirmedBy: confirmedBy,
  };
  await updateDoc(doc(db, "users", userId), paid);
  clearUsersCache();
  return paid;
}

// --- Messages ---
export async function deleteMessage(
  groupId: string,
  messageId: string,
): Promise<void> {
  const messageRef = doc(db, "groups", groupId, "messages", messageId);
  await deleteDoc(messageRef);
}

export async function toggleMessageLike(
  groupId: string,
  messageId: string,
  userId: string,
): Promise<void> {
  const messageRef = doc(db, "groups", groupId, "messages", messageId);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(messageRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const likedBy = (data.likedBy as string[]) || [];
    const isLiked = likedBy.includes(userId);
    const newLikedBy = isLiked
      ? likedBy.filter((id) => id !== userId)
      : [...likedBy, userId];
    transaction.update(messageRef, { likedBy: newLikedBy });
  });
}

// --- User deletion ---
export async function deleteUser(userId: string): Promise<void> {
  await deleteDoc(doc(db, "users", userId));
  usersMapCache?.delete(userId);
  clearUsersCache();
}

// --- Progress Posts ---
export async function createProgressPost(
  userId: string,
  title: string,
  imageUrls: string[],
  weight?: number,
): Promise<ProgressPost> {
  const postRef = doc(collection(db, "progressPosts"));
  const data: Omit<ProgressPost, "id"> = {
    userId,
    title,
    imageUrls,
    weight,
    createdAt: new Date().toISOString(),
  };
  await setDoc(postRef, data);

  // Log activity for first progress post (matching Next.js behavior)
  try {
    const user = await findUserById(userId);
    if (user) {
      const { createActivityAction } = await import("./actions");
      await createActivityAction(user, "first_progress_post");
    }
  } catch (error) {
    // Non-blocking - log but don't fail progress post creation
    console.warn("Failed to log first_progress_post activity:", error);
  }

  return { id: postRef.id, ...data };
}
export async function deleteProgressPost(
  postId: string,
  imageUrls: string[],
): Promise<void> {
  await deleteDoc(doc(db, "progressPosts", postId));
  await Promise.all(
    imageUrls.map((url) => deleteObject(ref(storage, url)).catch(() => { })),
  );
}
export function getProgressPostsForUser(
  userId: string,
  callback: (posts: ProgressPost[]) => void,
): Unsubscribe {
  const postsCol = collection(db, "progressPosts");
  const q = query(
    postsCol,
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) =>
      callback(
        snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as ProgressPost),
      ),
    (err) => {
      console.error(err);
      callback([]);
    },
  );
}

// --- Subscription management ---
// While a subscription is paused, its days left are kept in
// subscriptionPause.remainingMs (see lib/subscription.ts), so adding or
// removing days changes those instead of the end date.
export async function extendUserSubscription(
  userId: string,
  days: number,
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await runTransaction(db, async (transaction) => {
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error("User not found");
    const userData = userSnap.data() as User;
    const added = days * DAY_MS;

    if (userData.subscriptionPause?.since) {
      transaction.update(userRef, {
        "subscriptionPause.remainingMs": Math.max(0, userData.subscriptionPause.remainingMs || 0) + added,
      });
      return;
    }

    const now = new Date();
    const currentEndDate = userData.subscriptionEndDate
      ? new Date(userData.subscriptionEndDate)
      : now;
    // Extend from today if it already ran out (new users start at 1970), or
    // the renewal lands in the past and the account stays locked.
    const expired = currentEndDate <= now;
    const baseDate = expired ? now : currentEndDate;
    transaction.update(userRef, {
      subscriptionEndDate: new Date(baseDate.getTime() + added).toISOString(),
      // A renewal starts the inactivity count again (lib/inactivity.ts).
      ...(expired && INACTIVITY_BLOCK_ENABLED
        ? { inactivityReset: { at: now.toISOString(), reason: "renewal" } satisfies InactivityReset }
        : {}),
    });
  });
}

export async function reduceUserSubscription(
  userId: string,
  days: number,
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await runTransaction(db, async (transaction) => {
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error("User not found");
    const userData = userSnap.data() as User;
    const removed = days * DAY_MS;

    if (userData.subscriptionPause?.since) {
      transaction.update(userRef, {
        "subscriptionPause.remainingMs": Math.max(0, (userData.subscriptionPause.remainingMs || 0) - removed),
      });
      return;
    }

    const currentEndDate = userData.subscriptionEndDate
      ? new Date(userData.subscriptionEndDate)
      : new Date();
    transaction.update(userRef, {
      subscriptionEndDate: new Date(currentEndDate.getTime() - removed).toISOString(),
    });
  });
}

/**
 * Pauses a subscription until a trainer resumes it: the days left stop
 * running out. Unless `fullAccess`, the person can only use the feed and chat.
 */
export async function pauseUserSubscription(
  userId: string,
  options: { fullAccess: boolean; by: string },
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await runTransaction(db, async (transaction) => {
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error("User not found");
    const userData = userSnap.data() as User;
    if (userData.subscriptionPause?.since) return; // Already paused
    const pause: SubscriptionPause = {
      since: new Date().toISOString(),
      remainingMs: subscriptionTimeLeft(userData),
      fullAccess: options.fullAccess,
      by: options.by,
    };
    transaction.update(userRef, { subscriptionPause: pause });
  });
}

/** Resumes a paused subscription: it now ends after the days that were left. */
export async function resumeUserSubscription(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);
  await runTransaction(db, async (transaction) => {
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) throw new Error("User not found");
    const userData = userSnap.data() as User;
    if (!userData.subscriptionPause?.since) return; // Not paused
    const remaining = Math.max(0, userData.subscriptionPause.remainingMs || 0);
    transaction.update(userRef, {
      // With nothing left it stays expired (at the moment it was paused).
      subscriptionEndDate: remaining > 0
        ? new Date(Date.now() + remaining).toISOString()
        : userData.subscriptionEndDate || new Date(0).toISOString(),
      subscriptionPause: deleteField(),
      // The inactivity count starts again (lib/inactivity.ts).
      ...(INACTIVITY_BLOCK_ENABLED
        ? { inactivityReset: { at: new Date().toISOString(), reason: "resume" } satisfies InactivityReset }
        : {}),
    });
  });
}

/** Lifts an inactivity block: the 3 days start again from now (lib/inactivity.ts). */
export async function unblockInactiveUser(userId: string, by: string): Promise<InactivityReset> {
  const reset: InactivityReset = { at: new Date().toISOString(), reason: "unblock", by };
  await updateDoc(doc(db, "users", userId), { inactivityReset: reset });
  return reset;
}

/**
 * Remembers when the person last ticked a training day (lib/inactivity.ts).
 * Kept apart from the plan write and allowed to fail, so ticking a day still
 * works where the database rules don't allow this field yet.
 */
export async function recordTrainingChecked(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, "users", userId), { trainingCheckedAt: new Date().toISOString() });
  } catch (error) {
    console.warn("Could not save trainingCheckedAt:", error);
  }
}

/** Changes whether a paused user keeps full access to the app. */
export async function setPausedFullAccess(userId: string, fullAccess: boolean): Promise<void> {
  await updateDoc(doc(db, "users", userId), { "subscriptionPause.fullAccess": fullAccess });
}

// --- Training Plan Templates ---
export async function getTrainingPlanTemplates(): Promise<
  TrainingPlanTemplate[]
> {
  try {
    const templatesCollection = collection(db, "trainingPlanTemplates");
    const q = query(templatesCollection, orderBy("createdAt", "desc"));
    let querySnapshot;
    try {
      querySnapshot = await withTimeout(getDocs(q), 10000, "load templates");
    } catch (error) {
      console.warn("Templates are slow to load, using cached copies", error);
      querySnapshot = await getDocsFromCache(q);
    }

    const templates: TrainingPlanTemplate[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      templates.push({
        id: doc.id,
        name: data.name,
        description: data.description,
        plan: data.plan,
        createdBy: data.createdBy,
        createdAt: data.createdAt,
      } as TrainingPlanTemplate);
    });

    return templates;
  } catch (error) {
    if (typeof console !== "undefined" && console.error) {
      console.error("Error fetching training plan templates:", error);
    }
    throw new Error("Failed to fetch training plan templates");
  }
}
// --- System Logs ---
export async function logSystemError(data: {
  nnId: string;
  userId: string;
  userName: string;
  userRole: string;
  title: string;
  description: string;
  originalError?: string;
  stack?: string;
  file?: string;
}): Promise<void> {
  try {
    const logsCol = collection(db, "system_logs");
    await addDoc(logsCol, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  } catch (e) {
    console.warn("Failed to log system error:", e);
  }
}

export function getAllSystemLogs(
  callback: (logs: any[]) => void,
): Unsubscribe {
  const logsCol = collection(db, "system_logs");
  const q = query(logsCol, orderBy("timestamp", "desc"), limit(200));
  return onSnapshot(
    q,
    (snapshot) => {
      const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(logs);
    },
    (err) => {
      console.error("System logs error:", err);
      callback([]);
    },
  );
}

// Helper to remove undefined values for Firestore
export function sanitizeForFirestore(obj: any): any {
  if (obj === undefined) return null;
  if (obj === null) return null;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirestore);

  const newObj: any = {};
  for (const key in obj) {
    const val = obj[key];
    if (val === undefined) {
      newObj[key] = null;
    } else {
      newObj[key] = sanitizeForFirestore(val);
    }
  }
  return newObj;
}

// Debug helper to find undefined values
function findUndefinedValues(obj: any, path: string = ""): string[] {
  let undefinedPaths: string[] = [];
  if (obj === undefined) {
    undefinedPaths.push(path);
    return undefinedPaths;
  }
  if (obj === null || typeof obj !== "object") return [];

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      undefinedPaths = undefinedPaths.concat(
        findUndefinedValues(item, `${path}[${index}]`),
      );
    });
  } else {
    for (const key in obj) {
      if (obj[key] === undefined) {
        undefinedPaths.push(`${path}.${key}`);
      } else {
        undefinedPaths = undefinedPaths.concat(
          findUndefinedValues(obj[key], `${path}.${key}`),
        );
      }
    }
  }
  return undefinedPaths;
}

export async function createTrainingPlanTemplate(
  input: Omit<TrainingPlanTemplate, "id" | "createdAt">,
): Promise<TrainingPlanTemplate> {
  try {
    const templatesCollection = collection(db, "trainingPlanTemplates");

    const rawData = {
      name: input.name,
      description: input.description || "",
      plan: input.plan.map((day) => ({
        ...day,
        completed: false,
      })),
      createdBy: input.createdBy,
      createdAt: new Date().toISOString(),
    };

    // Debug check
    const undefinedFields = findUndefinedValues(rawData, "template");
    if (undefinedFields.length > 0) {
      console.error(
        "FOUND UNDEFINED FIELDS IN TEMPLATE CREATION:",
        undefinedFields,
      );
      console.warn(
        "These fields would cause a Firestore error. They will be sanitized to null.",
      );
    }

    const newTemplateData = sanitizeForFirestore(rawData);

    const docRef = await addDoc(templatesCollection, newTemplateData);

    return {
      id: docRef.id,
      ...newTemplateData,
    } as TrainingPlanTemplate;
  } catch (error) {
    console.error("Error creating training plan template:", error);
    throw new Error("Failed to create training plan template");
  }
}

export async function deleteTrainingPlanTemplate(
  templateId: string,
): Promise<void> {
  await deleteDoc(doc(db, "trainingPlanTemplates", templateId));
}

/**
 * Marks one training day done/undone. The plans are re-read first, so a
 * trainer's recent edits aren't overwritten by the copy the app loaded at
 * login. Returns the plans as saved.
 */
// Ticks of one person are saved one after another, in the order they were
// made: two quick taps can't overwrite each other.
let trainingSaveChain: Promise<unknown> = Promise.resolve();

function withDayCompleted(
  plans: UserTrainingPlan[],
  planId: string,
  dayId: string,
  completed: boolean,
): UserTrainingPlan[] {
  return plans.map((plan) =>
    plan.id !== planId
      ? plan
      : { ...plan, days: plan.days.map((day) => (day.id === dayId ? { ...day, completed } : day)) },
  );
}

/**
 * Ticks (or unticks) a training day. Only that day changes, on the plans as
 * they are on the server right now (a transaction), so it can't undo a
 * trainer's edit or another tick. Offline it falls back to the plans on this
 * device, which Firestore sends once the connection is back.
 */
export function setTrainingDayCompleted(
  userId: string,
  planId: string,
  dayId: string,
  completed: boolean,
  localPlans: UserTrainingPlan[],
): Promise<UserTrainingPlan[]> {
  const save = async (): Promise<UserTrainingPlan[]> => {
    const userRef = doc(db, "users", userId);
    let updated: UserTrainingPlan[];
    try {
      updated = await withTimeout(
        runTransaction(db, async (transaction) => {
          const snap = await transaction.get(userRef);
          const plans = (snap.data() as User | undefined)?.trainingPlans;
          const base = plans && plans.some((p) => p.id === planId) ? plans : localPlans;
          const next = withDayCompleted(base, planId, dayId, completed);
          transaction.update(userRef, { trainingPlans: next });
          return next;
        }),
        10_000,
        "save training day",
      );
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code === "permission-denied" || code === "not-found") throw error;
      diag("training day saved offline", { code });
      updated = withDayCompleted(localPlans, planId, dayId, completed);
      // Not awaited: offline it resolves only once the connection is back.
      void updateDoc(userRef, { trainingPlans: updated }).catch((e) =>
        diag("training day offline save failed", { code: (e as { code?: string })?.code }),
      );
    }
    if (completed) void recordTrainingChecked(userId);
    return updated;
  };
  const result = trainingSaveChain.then(save, save);
  trainingSaveChain = result.catch(() => {});
  return result;
}

/**
 * The weekly reset (every day unticked from Monday on), done on the plans as
 * they are on the server. Returns the plans, or null if the server can't be
 * reached (then nothing is reset now).
 */
export async function resetTrainingWeekIfNeeded(userId: string): Promise<UserTrainingPlan[] | null> {
  const userRef = doc(db, "users", userId);
  const startOfThisWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  try {
    return await withTimeout(
      runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        const data = snap.data() as User | undefined;
        const plans = data?.trainingPlans || [];
        const lastReset = data?.trainingPlanLastReset ? new Date(data.trainingPlanLastReset) : new Date(0);
        if (!(lastReset < startOfThisWeek) || plans.length === 0) return plans;
        const reset = plans.map((plan) => ({
          ...plan,
          days: plan.days.map((day) => ({ ...day, completed: false })),
        }));
        transaction.update(userRef, {
          trainingPlans: reset,
          trainingPlanLastReset: new Date().toISOString(),
        });
        return reset;
      }),
      10_000,
      "weekly training reset",
    );
  } catch {
    return null;
  }
}

export async function deleteUserTrainingPlan(
  userId: string,
  planId: string,
): Promise<void> {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    throw new Error("User not found");
  }

  const userData = userDoc.data() as User;
  const trainingPlans = userData.trainingPlans || [];
  const updatedPlans = trainingPlans.filter((plan) => plan.id !== planId);

  await updateDoc(userRef, {
    trainingPlans: updatedPlans,
  });
}

export async function applyTemplateToUsers(
  template: TrainingPlanTemplate,
  userIds: string[],
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  userIds.forEach((userId) => {
    // Create a NEW plan for EACH user with deep copies
    const planToApply: UserTrainingPlan = {
      id: uuidv4(),
      name: template.name,
      description: template.description,
      assignedAt: now,
      startWeek: 0,
      endWeek: 4,
      days: template.plan.map((day) => ({
        ...day,
        id: uuidv4(), // New ID for each day
        completed: false,
        blocks: (day.blocks || []).map((block) => {
          // Deep clone each block with new IDs
          const newBlock = JSON.parse(JSON.stringify(block));
          newBlock.id = uuidv4();

          // Handle nested blocks in loops
          if (newBlock.type === "loop" && newBlock.blocks) {
            newBlock.blocks = newBlock.blocks.map((nestedBlock: any) => {
              const newNested = JSON.parse(JSON.stringify(nestedBlock));
              newNested.id = uuidv4();
              return newNested;
            });
          }

          return newBlock;
        }),
      })),
    };

    const userRef = doc(db, "users", userId);
    batch.update(userRef, {
      trainingPlans: arrayUnion(planToApply),
    });
  });

  await batch.commit();
}

// --- Messages / Read Status ---
export async function markGroupAsRead(
  groupId: string,
  userId: string,
): Promise<void> {
  await updateDoc(doc(db, "groups", groupId), {
    [`lastRead.${userId}`]: new Date().toISOString(),
  });
}

export async function markGroupAsUnread(
  groupId: string,
  userId: string,
  timestamp: string,
): Promise<void> {
  await updateDoc(doc(db, "groups", groupId), {
    [`lastRead.${userId}`]: timestamp,
  });
}

// Mark messages as read for a user in a group
export async function markMessagesAsRead(
  groupId: string,
  userId: string,
): Promise<void> {
  const messagesRef = collection(db, "groups", groupId, "messages");
  const q = query(
    messagesRef,
    where("status", "==", "sent"),
    where("senderId", "!=", userId),
  );

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.docs.forEach((messageDoc) => {
    const messageRef = doc(db, "groups", groupId, "messages", messageDoc.id);
    const data = messageDoc.data();
    const readBy = data.readBy || [];

    // Only update if user hasn't read it yet
    if (!readBy.includes(userId)) {
      batch.update(messageRef, {
        status: "read",
        readBy: arrayUnion(userId),
      });
    }
  });

  await batch.commit();
}

// --- Food ---
// One shared collator: calling localeCompare per comparison is much slower
// when sorting thousands of foods.
const foodNameCollator = new Intl.Collator("hr");

// Loads in progress, so screens opening together share one download.
const foodsInflight = new Map<string, Promise<Food[]>>();

/** The foods list already loaded for this user in this app run, if any. */
export function peekFoods(userId: string): Food[] | undefined {
  if (!foodsCacheByUser.has(userId)) {
    const saved = readFoodsFromDevice(userId);
    if (saved) foodsCacheByUser.set(userId, saved);
  }
  return foodsCacheByUser.get(userId);
}

const builtInFoodIds = new Set((foodsData as Food[]).map((f) => f.id));

/** A food everyone can pick (not someone's own "Moje namirnice"). */
export function isSharedFood(food: Food): boolean {
  return food.isGlobal === true || (builtInFoodIds.has(food.id) && food.isGlobal !== false);
}

/**
 * Only the foods this user created (not shared ones), for "Moje namirnice".
 * Much lighter than getFoods, which also loads the whole shared database.
 */
export async function getMyFoods(userId: string): Promise<Food[]> {
  const snap = await getDocs(query(collection(db, "foods"), where("createdBy", "==", userId)));
  return snap.docs
    .map((d) => ({ ...(d.data() as Food), id: d.id }))
    .filter((f) => !f.deleted && !f.isGlobal)
    .sort((a, b) => foodNameCollator.compare(a.name, b.name));
}

export function getFoods(
  userId?: string,
  options?: { fetchAll?: boolean },
): Promise<Food[]> {
  const key = options?.fetchAll ? "__all__" : `user:${userId ?? ""}`;
  let pending = foodsInflight.get(key);
  if (!pending) {
    const load: Promise<Food[]> = loadFoods(userId, options).finally(() => {
      // Only if it's still this load (the cache may have been cleared since).
      if (foodsInflight.get(key) === load) foodsInflight.delete(key);
    });
    pending = load;
    foodsInflight.set(key, load);
  }
  return pending;
}

async function loadFoods(
  userId?: string,
  options?: { fetchAll?: boolean },
): Promise<Food[]> {
  try {
    // Return cached result unless fetchAll is requested
    const cacheKey = options?.fetchAll ? "__all__" : userId;
    if (!options?.fetchAll && foodsCacheByUser.has(cacheKey)) {
      return foodsCacheByUser.get(cacheKey)!;
    }
    if (!options?.fetchAll && userId) {
      const saved = readFoodsFromDevice(userId);
      if (saved) {
        foodsCacheByUser.set(cacheKey, saved);
        return saved;
      }
    }

    const started = performance.now();
    const generation = foodsCacheGeneration;
    // 1. Static foods (always global)
    let allFoods = [...(foodsData as Food[])];

    const foodsRef = collection(db, "foods");
    const firestoreFoodsMap = new Map<string, Food>();
    // Built-in foods a trainer deleted (they have a document with deleted).
    const deletedIds = new Set<string>();

    if (options?.fetchAll) {
      const snap = await getDocs(foodsRef);
      snap.docs.forEach((doc) => {
        const data = doc.data() as Food;
        if (data.deleted) deletedIds.add(doc.id);
        else firestoreFoodsMap.set(doc.id, { ...data, id: doc.id });
      });
    } else {
      // Only the foods this user can pick: global ones and their own. (This
      // used to download the whole collection, thousands of other users'
      // foods, and filter it here.)
      const [globalSnap, ownSnap] = await Promise.all([
        getDocs(query(foodsRef, where("isGlobal", "==", true))),
        userId
          ? getDocs(query(foodsRef, where("createdBy", "==", userId)))
          : Promise.resolve(null),
      ]);
      [...globalSnap.docs, ...(ownSnap?.docs ?? [])].forEach((doc) => {
        const data = doc.data() as Food;
        if (data.deleted) {
          deletedIds.add(doc.id);
          return;
        }
        firestoreFoodsMap.set(doc.id, { ...data, id: doc.id });
      });
    }
    allFoods = allFoods.filter((food) => !deletedIds.has(food.id));

    const firestoreFoods = Array.from(firestoreFoodsMap.values());
    allFoods = [...allFoods, ...firestoreFoods];

    // Deduplicate by ID just in case (static vs firestore shouldn't overlap but good practice)
    const uniqueFoods = Array.from(
      new Map(allFoods.map((item) => [item.id, item])).values(),
    ).sort((a, b) => foodNameCollator.compare(a.name, b.name));
    diag("foods loaded", { count: uniqueFoods.length, ms: since(started) });

    // Cache result for this user/mode (unless the cache was cleared meanwhile)
    if (!options?.fetchAll && generation === foodsCacheGeneration) {
      foodsCacheByUser.set(cacheKey, uniqueFoods);
      if (userId) saveFoodsOnDevice(userId, uniqueFoods);
    }

    return uniqueFoods;
  } catch (e) {
    console.error("Error fetching foods: ", e);
    return [];
  }
}

// --- Activities ---
export function getActivitiesForUser(
  userId: string,
  callback: (activities: Activity[]) => void,
): Unsubscribe {
  const activitiesRef = collection(db, "activities");
  const q = query(
    activitiesRef,
    where("userId", "==", userId),
    orderBy("timestamp", "desc"),
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const activities = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Activity,
      );
      callback(activities);
    },
    (serverError) => {
      console.error(
        `Permission denied fetching activities for user.`,
        serverError,
      );
      callback([]);
    },
  );

  return unsubscribe;
}

export function getAllActivities(
  callback: (activities: Activity[], hasMore: boolean, fromCache: boolean) => void,
  options: { limitCount?: number; onError?: (error: Error) => void } = {},
): Unsubscribe {
  const { limitCount, onError } = options;
  // Activities already carry the user's name and avatar. (This used to
  // download every user first, which is what kept the page spinning.)
  const activitiesRef = collection(db, "activities");
  const q = limitCount
    ? query(activitiesRef, orderBy("timestamp", "desc"), limit(limitCount))
    : query(activitiesRef, orderBy("timestamp", "desc"));

  const started = performance.now();
  let first = true;
  return onServerSnapshot(
    q,
    (snapshot) => {
      const activities = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Activity,
      );
      if (first) {
        first = false;
        diag("activities loaded", { count: activities.length, ms: since(started) });
      }
      callback(activities, !!limitCount && snapshot.size >= limitCount, snapshot.metadata.fromCache);
    },
    (serverError) => {
      diag("activities listener failed", { code: (serverError as { code?: string }).code });
      console.error("Failed to load activities.", serverError);
      if (onError) onError(serverError);
      else callback([], false, false);
    },
  );
}

// --- Meal Logs & Templates ---

export async function getMealLog(userId: string, date: string): Promise<MealLog | null> {
  const q = query(
    collection(db, "mealLogs"),
    where("userId", "==", userId),
    where("date", "==", date)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as MealLog;
}

// Saves for the same day run one after another: two overlapping saves (auto-save
// plus the save button) each saw "no log yet" and created a duplicate.
const mealLogSaveQueue = new Map<string, Promise<void>>();

// Water intake (HydrationCard) is stored on the same day's log, so it shares the queue.
function queueMealLogWrite(userId: string, date: string, write: () => Promise<void>): Promise<void> {
  const key = `${userId}_${date}`;
  const previous = mealLogSaveQueue.get(key) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(write);
  mealLogSaveQueue.set(key, next);
  next.finally(() => {
    if (mealLogSaveQueue.get(key) === next) mealLogSaveQueue.delete(key);
  }).catch(() => undefined);
  return next;
}

export function saveMealLog(
  userId: string,
  date: string,
  meals: Meal[],
  dailyGoals?: { calories: number; protein: number; carbs: number; fat: number }
): Promise<void> {
  return queueMealLogWrite(userId, date, () => writeMealLog(userId, date, meals, dailyGoals));
}

/**
 * Adds `deltaMl` (negative to take some back) to the water drunk on `date`.
 * An increment rather than a new total, so taps on two devices both count.
 */
export function addWaterIntake(
  userId: string,
  date: string,
  deltaMl: number,
  goalMl: number,
): Promise<void> {
  return queueMealLogWrite(userId, date, async () => {
    const existing = await getMealLog(userId, date);
    const now = new Date().toISOString();
    if (existing) {
      // Never below zero, even if another device took some back meanwhile.
      const change = Math.max(deltaMl, -(existing.waterMl ?? 0));
      await updateDoc(doc(db, "mealLogs", existing.id), {
        waterMl: increment(change),
        waterGoalMl: goalMl,
        updatedAt: now,
      });
    } else {
      await addDoc(collection(db, "mealLogs"), {
        userId,
        date,
        waterMl: Math.max(0, deltaMl),
        waterGoalMl: goalMl,
        updatedAt: now,
        createdAt: now,
      });
    }
  });
}

/** Saves the user's water goal on `date` (it carries over to later days). */
export function saveWaterGoal(userId: string, date: string, goalMl: number): Promise<void> {
  return queueMealLogWrite(userId, date, async () => {
    const existing = await getMealLog(userId, date);
    const now = new Date().toISOString();
    if (existing) {
      await updateDoc(doc(db, "mealLogs", existing.id), { waterGoalMl: goalMl, updatedAt: now });
    } else {
      await addDoc(collection(db, "mealLogs"), {
        userId,
        date,
        waterMl: 0,
        waterGoalMl: goalMl,
        updatedAt: now,
        createdAt: now,
      });
    }
  });
}

export interface WaterDay {
  ml: number;
  goalMl?: number;
}

/** Water per day (yyyy-MM-dd) from `start` to `end`, both included. Days with no log are missing. */
export async function getWaterHistory(
  userId: string,
  start: string,
  end: string,
): Promise<Map<string, WaterDay>> {
  const q = query(
    collection(db, "mealLogs"),
    where("userId", "==", userId),
    where("date", ">=", start),
    where("date", "<=", end)
  );
  const snap = await getDocs(q);
  const days = new Map<string, WaterDay>();
  for (const d of snap.docs) {
    const log = d.data() as MealLog;
    if (typeof log.waterMl !== "number" && typeof log.waterGoalMl !== "number") continue;
    const prev = days.get(log.date);
    days.set(log.date, {
      ml: (prev?.ml ?? 0) + Math.max(0, log.waterMl ?? 0),
      goalMl: log.waterGoalMl ?? prev?.goalMl,
    });
  }
  return days;
}

async function writeMealLog(
  userId: string,
  date: string,
  meals: Meal[],
  dailyGoals?: { calories: number; protein: number; carbs: number; fat: number }
): Promise<void> {
  const existing = await getMealLog(userId, date);
  const now = new Date().toISOString();

  const data = {
    userId,
    date,
    meals,
    dailyGoals: dailyGoals || null,
    updatedAt: now,
  };

  if (existing) {
    await updateDoc(doc(db, "mealLogs", existing.id), sanitizeForFirestore(data));
  } else {
    const newLog = {
      ...data,
      createdAt: now,
    };
    await addDoc(collection(db, "mealLogs"), sanitizeForFirestore(newLog));
  }
}

export async function getDatesWithMeals(userId: string, startOfMonth: string, endOfMonth: string): Promise<string[]> {
  const q = query(
    collection(db, "mealLogs"),
    where("userId", "==", userId),
    where("date", ">=", startOfMonth),
    where("date", "<=", endOfMonth)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => d.data() as MealLog)
    .filter(log => log.meals && log.meals.some((meal: any) => meal.items && meal.items.length > 0))
    .map(log => log.date);
}

export async function getMealTemplates(userId: string): Promise<MealTemplate[]> {
  const q = query(
    collection(db, "mealTemplates"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  const templates = snap.docs.map(d => ({ id: d.id, ...d.data() } as MealTemplate));

  // Sort locally to avoid requiring a composite index
  return templates.sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export async function saveMealTemplate(template: Omit<MealTemplate, "id" | "createdAt">): Promise<MealTemplate> {
  const data = {
    ...template,
    createdAt: new Date().toISOString(),
  };
  const docRef = await addDoc(collection(db, "mealTemplates"), sanitizeForFirestore(data));
  return { id: docRef.id, ...data } as MealTemplate;
}

export async function updateMealTemplate(templateId: string, template: Partial<MealTemplate>): Promise<void> {
  await updateDoc(doc(db, "mealTemplates", templateId), sanitizeForFirestore(template));
}

export async function deleteMealTemplate(templateId: string): Promise<void> {
  await deleteDoc(doc(db, "mealTemplates", templateId));
}
